<script setup>
import { computed, ref, watch, onUnmounted } from 'vue'
import { t, availableLanguages, getLanguage, setLanguage } from '../services/i18nService'
import { getTheme, setTheme } from '../services/themeService'
import { getTextSize, setTextSize } from '../services/textSizeService'
import { getNavLayout, setNavLayout } from '../services/navLayoutService'
import { getMobilePanel, setMobilePanel } from '../services/mobilePanelService'
import { revokeAllSessions, fetchActiveSessions, revokeSessionById, fetchTrustedDevices, revokeTrustedDevice } from '../services/authService'
import { disconnectClient as disconnectCentrifugo } from '../services/centrifugoService'
import {
  changePassword,
  deleteUserAvatar,
  fetchUserProfile,
  requestEmailChange,
  updateUserProfile,
  uploadUserAvatar,
  getTelegramLinkCode,
  unlinkTelegramAccount,
  getTelegramAccounts,
  getNotificationPreferences,
  updateNotificationPreferences,
  fetchApiTokens,
  createApiToken,
  revokeApiToken,
  updateApiToken,
} from '../services/portalService'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  // Только настройки вида (язык, тема, размер текста, меню, панель на карте) - для суб-пользователя.
  // Всё это хранится в браузере, а профиль, пароль, сессии и API-токены владельца ему недоступны
  // (сервер отвечает 403), поэтому вкладки, аватар, имя, email и кнопка сохранения скрыты
  displayOnly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close', 'updated'])

const loading = ref(false)
const profile = ref(null)
const profileName = ref('')
const pendingEmail = ref('')
const customFields = ref([{ key: '', value: '' }])
const showCustomFields = ref(false)

const avatarPreview = ref('')
const avatarUploading = ref(false)
const avatarDeleting = ref(false)
const avatarError = ref('')
const avatarMessage = ref('')

const notificationLang = ref('uk') // Язык уведомлений (email, Telegram, in-app) — хранится в user.lang на сервере

// Язык интерфейса (per-device, как тема): localStorage client_lang, тот же, что выбирают на входе.
// Язык уведомлений (user.lang) — отдельная настройка на странице «Уведомления», здесь её не трогаем.
const interfaceLanguage = ref(getLanguage())
const languageSwitching = ref(false)
// Названия языков не переводим: попавший в чужой язык должен узнать свой
const languageNames = { uk: 'Українська', ru: 'Русский', en: 'English', pl: 'Polski' }
async function onInterfaceLanguageChange(value) {
  if (value === interfaceLanguage.value || languageSwitching.value) return
  languageSwitching.value = true
  interfaceLanguage.value = value
  try {
    // Словарь не реактивный (t() — обычная функция), поэтому применяем перезагрузкой страницы.
    // setLanguage сохраняет выбор только после успешной загрузки словаря
    await setLanguage(value)
    window.location.reload()
  } catch {
    // Словарь не загрузился — остаёмся на текущем языке
    interfaceLanguage.value = getLanguage()
    languageSwitching.value = false
  }
}

// Тема оформления: auto|light|dark. Хранится в localStorage (per-device).
const themePreference = ref(getTheme())
function onThemeChange(value) {
  themePreference.value = value
  setTheme(value)
}

// Размер текста (per-device, как тема): масштаб корневого шрифта 100 / 112.5 / 125%
const textSizePreference = ref(getTextSize())
const textSizeOptions = [
  { value: 'normal', icon: 'text_fields', labelKey: 'settings.textSize.normal' },
  { value: 'large', icon: 'format_size', labelKey: 'settings.textSize.large' },
  { value: 'xlarge', icon: 'text_increase', labelKey: 'settings.textSize.xlarge' },
]
function onTextSizeChange(value) {
  textSizePreference.value = value
  setTextSize(value)
}

// Режим главного меню (per-device, применяется только на desktop >= 960px)
const navLayoutPreference = ref(getNavLayout())
const navLayoutOptions = [
  { value: 'topbar', icon: 'horizontal_split', labelKey: 'settings.navLayout.topbar' },
  { value: 'side', icon: 'view_sidebar', labelKey: 'settings.navLayout.side' },
  { value: 'side-icons', icon: 'vertical_split', labelKey: 'settings.navLayout.sideIcons' },
]
function onNavLayoutChange(value) {
  navLayoutPreference.value = value
  setNavLayout(value)
}

// Панель списка на карте для телефона (per-device, применяется только < 768px,
// на страницах Транспорт и Геозоны)
const mobilePanelPreference = ref(getMobilePanel())
const mobilePanelOptions = [
  { value: 'sheet', icon: 'call_to_action', labelKey: 'settings.mobilePanel.sheet' },
  { value: 'drawer', icon: 'view_sidebar', labelKey: 'settings.mobilePanel.drawer' },
]
function onMobilePanelChange(value) {
  mobilePanelPreference.value = value
  setMobilePanel(value)
}

const profileError = ref('')
const savingProfile = ref(false)

const emailInput = ref('')
const emailError = ref('')
const emailMessage = ref('')
const emailSaving = ref(false)

const technicalEmailInput = ref('')
// Адрес для счетов (бухгалтерия клиента); пусто = счета на email профиля
const invoiceEmailInput = ref('')

const showPasswordChange = ref(false)
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const passwordError = ref('')
const passwordMessage = ref('')
const passwordSaving = ref(false)

// «Вийти з усіх пристроїв» — підтвердження + виклик API + редирект на login
const revokeAllConfirming = ref(false)
const revokeAllSaving = ref(false)
const revokeAllError = ref('')

// Список активних сесій (по одному на пристрій)
const sessions = ref([])
const sessionsLoading = ref(false)
const sessionsError = ref('')
const revokingSessionId = ref(null)

// Довірені пристрої (device-trust, кука client_dt)
const trustedDevices = ref([])
const trustedDevicesLoading = ref(false)
const trustedDevicesError = ref('')
const revokingDeviceId = ref(null)
const deviceConfirmId = ref(null) // двокроковий відклик: перший клік = підтвердження

// Поддержка нескольких Telegram аккаунтов
const telegramAccounts = ref([])
const maxTelegramAccounts = ref(5)
const canAddMoreTelegram = computed(() => telegramAccounts.value.length < maxTelegramAccounts.value)
const hasTelegramLinked = computed(() => telegramAccounts.value.length > 0)
const telegramLinkLoading = ref(false)
const telegramDeepLink = ref('')
const telegramCode = ref('')
const telegramError = ref('')
const telegramMessage = ref('')
const telegramPollingInterval = ref(null)
const notificationPrefs = ref({})
const notificationEventTypes = ref([])
const notificationChannels = ref([])
const notifPrefsSaving = ref(false)
const notifPrefsError = ref('')
const notifPrefsMessage = ref('')

const MAX_AVATAR_BYTES = 3.5 * 1024 * 1024
const allowedMime = ['image/jpeg', 'image/png', 'image/webp']

const hasAvatar = computed(() => Boolean(avatarPreview.value))

// Вкладки: профиль, оповещения, пароль, API-токены
const activeTab = ref('profile')

// === API-токени ===
const apiTokens = ref([])
const loadingTokens = ref(false)
const showCreateForm = ref(false)
const newTokenName = ref('')
const newTokenScopes = ref([])
const creatingToken = ref(false)
const newlyCreatedToken = ref(null)
const revokingTokenId = ref(null)
const editingTokenId = ref(null)
const editTokenName = ref('')
const editTokenScopes = ref([])
const savingToken = ref(false)

// Групування scope-ів за категорією для відображення у формі
const TOKEN_SCOPE_GROUPS = [
  { label: 'settings.api.scopeGroups.devices',     scopes: ['devices:read', 'devices:write'] },
  { label: 'settings.api.scopeGroups.telemetry',   scopes: ['telemetry:read'] },
  { label: 'settings.api.scopeGroups.geozones',    scopes: ['geozones:read', 'geozones:write'] },
  { label: 'settings.api.scopeGroups.drivers',     scopes: ['drivers:read', 'drivers:write'] },
  { label: 'settings.api.scopeGroups.trailers',    scopes: ['trailers:read', 'trailers:write'] },
  { label: 'settings.api.scopeGroups.maintenance', scopes: ['maintenance:read', 'maintenance:write'] },
  { label: 'settings.api.scopeGroups.other',       scopes: ['reports:read', 'notifications:read', 'billing:read', 'webhooks:manage'] },
  { label: 'settings.api.scopeGroups.etoll',       scopes: ['etoll:read'] },
]

// Проверка изменения email (показываем кнопку только если email изменился)
const isEmailChanged = computed(() => {
  const currentEmail = (profile.value?.pendingEmail || profile.value?.email || '').toLowerCase().trim()
  const inputEmail = emailInput.value.toLowerCase().trim()
  return inputEmail !== currentEmail && inputEmail.length > 0
})

const mapErrorMessage = (code, fallback) => {
  switch (code) {
    case 'custom_field_key_required':
      return t('settings.profile.keyRequired')
    case 'avatar_too_large':
    case 'avatar_too_large_after_resize':
      return t('settings.avatar.tooLarge')
    case 'avatar_type_invalid':
    case 'avatar_parse_failed':
    case 'avatar_upload_error':
    case 'avatar_read_error':
    case 'avatar_encode_failed':
      return t('settings.errors.avatarFailed')
    case 'invalid_email':
      return t('settings.email.invalid')
    case 'email_same':
      return t('settings.email.same')
    case 'email_taken':
      return t('settings.email.taken')
    case 'invalid_technical_email':
      return t('settings.technicalEmail.invalid')
    case 'invalid_invoice_email':
      return t('settings.invoiceEmail.invalid')
    case 'current_password_invalid':
      return t('settings.password.currentInvalid')
    case 'password_too_short':
      return t('settings.password.tooShort')
    case 'password_too_simple':
      return t('settings.password.tooSimple')
    case 'password_same_as_login':
      return t('settings.password.sameAsLogin')
    default:
      return fallback
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      // Выбор мог поменяться, пока модалка была закрыта (меню сворачивают кнопкой в шапке): берём сохранённый
      themePreference.value = getTheme()
      textSizePreference.value = getTextSize()
      navLayoutPreference.value = getNavLayout()
      mobilePanelPreference.value = getMobilePanel()
      // В режиме «только вид» профиль не грузим: показывать из него нечего
      if (props.displayOnly) {
        resetFormState()
      } else {
        loadProfile()
      }
    } else {
      // Останавливаем polling при закрытии модалки
      if (telegramPollingInterval.value) {
        clearInterval(telegramPollingInterval.value)
        telegramPollingInterval.value = null
      }
      resetFormState()
      // reset секції безпеки
      sessions.value = []
      sessionsError.value = ''
      revokeAllConfirming.value = false
      revokeAllError.value = ''
      trustedDevices.value = []
      trustedDevicesError.value = ''
      deviceConfirmId.value = null
    }
  }
)

// Підвантажуємо сесії та довірені пристрої при відкритті табу «Безпека»
watch(activeTab, (tab) => {
  if (tab === 'security' && props.open) {
    loadSessions()
    loadTrustedDevices()
  }
})

// Cleanup при unmount
onUnmounted(() => {
  if (telegramPollingInterval.value) {
    clearInterval(telegramPollingInterval.value)
    telegramPollingInterval.value = null
  }
})

const resetFormState = () => {
  profileError.value = ''
  emailError.value = ''
  emailMessage.value = ''
  avatarError.value = ''
  avatarMessage.value = ''
  passwordError.value = ''
  passwordMessage.value = ''
  currentPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
  showPasswordChange.value = false
  activeTab.value = 'profile'
  // Сброс состояния API-токенов
  apiTokens.value = []
  showCreateForm.value = false
  newTokenName.value = ''
  newTokenScopes.value = []
  newlyCreatedToken.value = null
  revokingTokenId.value = null
  editingTokenId.value = null
  editTokenName.value = ''
  editTokenScopes.value = []
  savingToken.value = false
  // Сброс состояния блока уведомлений
  telegramError.value = ''
  telegramMessage.value = ''
  telegramDeepLink.value = ''
  telegramCode.value = ''
  notifPrefsError.value = ''
  notifPrefsMessage.value = ''
}

const handleClose = () => {
  emit('close')
}

const loadProfile = async () => {
  loading.value = true
  resetFormState()
  try {
    const data = await fetchUserProfile()
    profile.value = data.user
    profileName.value = data.user?.name || ''
    pendingEmail.value = data.user?.pendingEmail || ''
    emailInput.value = data.user?.pendingEmail || data.user?.email || ''
    technicalEmailInput.value = data.user?.technicalEmail || ''
    invoiceEmailInput.value = data.user?.invoiceEmail || ''
    notificationLang.value = data.user?.lang || 'uk'
    const avatar = data.user?.profile?.avatar
    avatarPreview.value =
      avatar && avatar.base64 ? `data:${avatar.mimeType};base64,${avatar.base64}` : ''
    const fields = data.user?.profile?.customFields
    customFields.value =
      Array.isArray(fields) && fields.length ? fields.map(normalizeField) : [{ key: '', value: '' }]
    // Показываем дополнительные поля если они есть
    showCustomFields.value = Array.isArray(fields) && fields.length > 0 && fields.some(f => f.key || f.value)
    // Загружаем список Telegram аккаунтов из профиля
    telegramAccounts.value = data.user?.telegramAccounts || []
    // Загружаем настройки уведомлений
    await loadNotificationData()
  } catch (err) {
    profileError.value = err?.message || t('settings.errors.loadFailed')
  } finally {
    loading.value = false
  }
}

const normalizeField = (field) => ({
  key: String(field?.key ?? '').trim(),
  value: String(field?.value ?? '').trim(),
})

const addField = () => {
  customFields.value.push({ key: '', value: '' })
}

const removeField = (index) => {
  if (customFields.value.length === 1) {
    customFields.value = [{ key: '', value: '' }]
    return
  }
  customFields.value.splice(index, 1)
}

const saveProfile = async () => {
  profileError.value = ''
  const preparedFields = []

  for (const field of customFields.value) {
    const normalized = normalizeField(field)
    if (!normalized.key && !normalized.value) continue
    if (!normalized.key) {
      profileError.value = t('settings.profile.keyRequired')
      return
    }
    preparedFields.push(normalized)
  }

  // Валидация технического email
  const techEmail = technicalEmailInput.value
    .trim()
    .replace(/[\s\u00A0\u200B-\u200D\u2060\uFEFF]+/g, '')
  if (techEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(techEmail)) {
    profileError.value = t('settings.technicalEmail.invalid')
    return
  }

  // Валидация адреса для счетов
  const invoiceEmail = invoiceEmailInput.value
    .trim()
    .replace(/[\s\u00A0\u200B-\u200D\u2060\uFEFF]+/g, '')
  if (invoiceEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceEmail)) {
    profileError.value = t('settings.invoiceEmail.invalid')
    return
  }

  savingProfile.value = true
  try {
    const payload = {
      name: profileName.value.trim(),
      customFields: preparedFields,
      technicalEmail: techEmail || null,
      // В режиме «только вид» поле скрыто: не передаём, чтобы не стереть
      ...(props.displayOnly ? {} : { invoiceEmail }),
      notificationLang: notificationLang.value,
    }
    const data = await updateUserProfile(payload)
    profile.value = data.user
    customFields.value =
      Array.isArray(data.user?.profile?.customFields) && data.user.profile.customFields.length
        ? data.user.profile.customFields.map(normalizeField)
        : [{ key: '', value: '' }]
    profileName.value = data.user?.name || ''
    technicalEmailInput.value = data.user?.technicalEmail || ''
    invoiceEmailInput.value = data.user?.invoiceEmail || ''
    profileError.value = ''
    avatarMessage.value = ''
    emailMessage.value = ''
    emit('updated')
    handleClose()
  } catch (err) {
    profileError.value = mapErrorMessage(err?.message, t('settings.errors.saveFailed'))
  } finally {
    savingProfile.value = false
  }
}

const onAvatarChange = async (event) => {
  avatarError.value = ''
  avatarMessage.value = ''
  const [file] = event.target.files || []
  event.target.value = ''
  if (!file) return

  if (!allowedMime.includes(file.type)) {
    avatarError.value = t('settings.avatar.invalidType')
    return
  }
  if (file.size > MAX_AVATAR_BYTES) {
    avatarError.value = t('settings.avatar.tooLarge')
    return
  }

  avatarUploading.value = true
  try {
    const response = await uploadUserAvatar(file)
    const avatar = response.avatar
    avatarPreview.value =
      avatar && avatar.base64 ? `data:${avatar.mimeType};base64,${avatar.base64}` : ''
    avatarMessage.value = t('settings.avatar.updated')
    emit('updated')
  } catch (err) {
    avatarError.value = mapErrorMessage(err?.message, t('settings.errors.avatarFailed'))
  } finally {
    avatarUploading.value = false
  }
}

const onAvatarDelete = async () => {
  avatarError.value = ''
  avatarMessage.value = ''
  avatarDeleting.value = true
  try {
    await deleteUserAvatar()
    avatarPreview.value = ''
    avatarMessage.value = t('settings.avatar.deleted')
    emit('updated')
  } catch (err) {
    avatarError.value = mapErrorMessage(err?.message, t('settings.errors.avatarFailed'))
  } finally {
    avatarDeleting.value = false
  }
}

const submitEmailChange = async () => {
  emailError.value = ''
  emailMessage.value = ''
  const targetEmail = emailInput.value
    .trim()
    .replace(/[\s\u00A0\u200B-\u200D\u2060\uFEFF]+/g, '')
  emailInput.value = targetEmail

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(targetEmail)) {
    emailError.value = t('settings.email.invalid')
    return
  }

  if (profile.value?.email && profile.value.email === targetEmail) {
    emailError.value = t('settings.email.same')
    return
  }

  emailSaving.value = true
  try {
    const result = await requestEmailChange(targetEmail)
    pendingEmail.value = result.pendingEmail || targetEmail
    emailMessage.value = t('settings.email.updated')
  } catch (err) {
    emailError.value = mapErrorMessage(err?.message, t('settings.email.error'))
  } finally {
    emailSaving.value = false
  }
}

const pendingEmailLabel = computed(() => {
  if (!pendingEmail.value) return ''
  return t('settings.email.pending', { email: pendingEmail.value })
})

const submitPasswordChange = async () => {
  passwordError.value = ''
  passwordMessage.value = ''

  if (!currentPassword.value) {
    passwordError.value = t('settings.password.current') + ' required'
    return
  }

  if (newPassword.value.length < 6) {
    passwordError.value = t('settings.password.tooShort')
    return
  }

  if (newPassword.value !== confirmPassword.value) {
    passwordError.value = t('settings.password.mismatch')
    return
  }

  passwordSaving.value = true
  try {
    await changePassword(currentPassword.value, newPassword.value)
    passwordMessage.value = t('settings.password.success')
    currentPassword.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
  } catch (err) {
    passwordError.value = mapErrorMessage(err?.message, t('settings.password.error'))
  } finally {
    passwordSaving.value = false
  }
}

// Завантажує список активних сесій. Викликається при відкритті табу «Пароль».
const loadSessions = async () => {
  sessionsError.value = ''
  sessionsLoading.value = true
  try {
    sessions.value = await fetchActiveSessions()
  } catch (err) {
    sessionsError.value = mapErrorMessage(err?.message, t('settings.security.sessionsError'))
  } finally {
    sessionsLoading.value = false
  }
}

// Форматує last_used / issued_at у відносну дату
const formatSessionTime = (iso) => {
  if (!iso) return ''
  // Бекенд віддає DATETIME як 'YYYY-MM-DD HH:MM:SS' (UTC) — додаємо Z для коректного парсингу
  const date = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z')
  if (isNaN(date.getTime())) return iso
  const lang = notificationLang.value || 'uk'
  return date.toLocaleString(lang === 'uk' ? 'uk-UA' : lang === 'ru' ? 'ru-RU' : 'en-US', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// Підпис пристрою: OS + Browser, fallback на raw UA якщо парсер нічого не дав
const sessionDeviceLabel = (s) => {
  const parts = []
  if (s.device?.deviceModel && s.device.deviceModel !== '') parts.push(s.device.deviceModel)
  if (s.device?.os && s.device.os !== 'unknown') {
    parts.push(s.device.osVersion ? `${s.device.os} ${s.device.osVersion}` : s.device.os)
  }
  if (s.device?.browser && s.device.browser !== 'unknown') parts.push(s.device.browser)
  return parts.length ? parts.join(' · ') : (s.userAgent || t('settings.security.unknownDevice'))
}

// Іконка типу пристрою
const sessionDeviceIcon = (s) => {
  const t = s.device?.deviceType
  if (t === 'mobile') return 'smartphone'
  if (t === 'tablet') return 'tablet'
  return 'computer'
}

// Завантажує довірені пристрої (device-trust). Викликається при відкритті табу «Безпека».
const loadTrustedDevices = async () => {
  trustedDevicesError.value = ''
  trustedDevicesLoading.value = true
  deviceConfirmId.value = null
  try {
    trustedDevices.value = await fetchTrustedDevices()
  } catch (err) {
    trustedDevicesError.value = mapErrorMessage(err?.message, t('settings.trustedDevices.error'))
  } finally {
    trustedDevicesLoading.value = false
  }
}

// Відклик довіри: перший клік — підтвердження, другий — виконує. Сесію не рвемо,
// тому редиректу нема навіть для поточного пристрою (нова мітка видасться при наступному вході).
const revokeOneDevice = async (device) => {
  if (deviceConfirmId.value !== device.id) {
    deviceConfirmId.value = device.id
    return
  }
  trustedDevicesError.value = ''
  revokingDeviceId.value = device.id
  try {
    await revokeTrustedDevice(device.id)
    trustedDevices.value = trustedDevices.value.filter((d) => d.id !== device.id)
  } catch (err) {
    trustedDevicesError.value = mapErrorMessage(err?.message, t('settings.trustedDevices.revokeError'))
  } finally {
    revokingDeviceId.value = null
    deviceConfirmId.value = null
  }
}

// Відкликає одну конкретну сесію. Якщо це поточна — після успіху йдемо на /login.
const revokeOneSession = async (session) => {
  sessionsError.value = ''
  revokingSessionId.value = session.id
  try {
    const res = await revokeSessionById(session.id)
    if (res?.isCurrent) {
      // Поточна сесія мертва — жорсткий вихід
      disconnectCentrifugo()
      window.location.replace('/login')
      return
    }
    // Прибираємо з локального списку без додаткового запиту
    sessions.value = sessions.value.filter((s) => s.id !== session.id)
  } catch (err) {
    sessionsError.value = mapErrorMessage(err?.message, t('settings.security.revokeOneError'))
  } finally {
    revokingSessionId.value = null
  }
}

// Відкликає всі refresh-токени поточного користувача (включно з поточною сесією) і виходить з порталу.
const submitRevokeAllSessions = async () => {
  revokeAllError.value = ''
  revokeAllSaving.value = true
  try {
    await revokeAllSessions()
    // Поточна сесія вже мертва — закриваємо WS і йдемо на логін через жорсткий редирект,
    // щоб скинути весь стан компонентів
    disconnectCentrifugo()
    window.location.replace('/login')
  } catch (err) {
    revokeAllError.value = mapErrorMessage(err?.message, t('settings.security.revokeAllError'))
    revokeAllSaving.value = false
    revokeAllConfirming.value = false
  }
}

// ======================
// TELEGRAM & NOTIFICATIONS
// ======================

const loadNotificationData = async () => {
  try {
    const prefsRes = await getNotificationPreferences()
    notificationPrefs.value = prefsRes.preferences || {}
    notificationEventTypes.value = prefsRes.eventTypes || []
    notificationChannels.value = prefsRes.channels || []
  } catch (err) {
    console.error('Failed to load notification preferences:', err)
  }
}

// Останавливает polling для проверки привязки Telegram
const stopTelegramPolling = () => {
  if (telegramPollingInterval.value) {
    clearInterval(telegramPollingInterval.value)
    telegramPollingInterval.value = null
  }
}

// Запускает polling для автоматического обнаружения привязки
const startTelegramPolling = () => {
  stopTelegramPolling()
  const initialCount = telegramAccounts.value.length

  telegramPollingInterval.value = setInterval(async () => {
    try {
      const result = await getTelegramAccounts()
      if (result.accounts && result.accounts.length > initialCount) {
        // Новый аккаунт привязан - обновляем список
        telegramAccounts.value = result.accounts
        telegramDeepLink.value = ''
        telegramCode.value = ''
        telegramMessage.value = t('settings.notifications.telegram.linked')
        stopTelegramPolling()
      }
    } catch (err) {
      // Игнорируем ошибки polling
    }
  }, 3000) // Проверяем каждые 3 секунды
}

const generateTelegramLink = async () => {
  telegramError.value = ''
  telegramMessage.value = ''
  telegramLinkLoading.value = true
  try {
    const result = await getTelegramLinkCode()
    telegramDeepLink.value = result.deepLink
    telegramCode.value = result.code
    // Запускаем polling для автоматического обнаружения привязки
    startTelegramPolling()
  } catch (err) {
    if (err?.message === 'telegram_not_configured') {
      telegramError.value = t('settings.notifications.telegram.notConfigured')
    } else if (err?.message === 'telegram_limit_reached') {
      telegramError.value = t('settings.notifications.telegram.limitReached')
    } else {
      telegramError.value = t('settings.notifications.telegram.error')
    }
  } finally {
    telegramLinkLoading.value = false
  }
}

const handleUnlinkTelegram = async (telegramId) => {
  telegramError.value = ''
  telegramMessage.value = ''
  try {
    await unlinkTelegramAccount(telegramId)
    // Удаляем аккаунт из локального списка
    telegramAccounts.value = telegramAccounts.value.filter(acc => acc.id !== telegramId)
    telegramDeepLink.value = ''
    telegramCode.value = ''
    telegramMessage.value = t('settings.notifications.telegram.unlinked')
  } catch (err) {
    telegramError.value = t('settings.notifications.telegram.unlinkError')
  }
}

const togglePref = (eventType, channel, enabled) => {
  if (!notificationPrefs.value[eventType]) {
    notificationPrefs.value[eventType] = {}
  }
  notificationPrefs.value[eventType][channel] = enabled
}

// ======================
// API TOKENS
// ======================

const loadApiTokens = async () => {
  loadingTokens.value = true
  try {
    const data = await fetchApiTokens()
    apiTokens.value = Array.isArray(data.data) ? data.data : []
  } catch (err) {
    console.error('Failed to load API tokens:', err)
  } finally {
    loadingTokens.value = false
  }
}

const handleCreateToken = async () => {
  if (!newTokenName.value.trim() || newTokenScopes.value.length === 0) return
  creatingToken.value = true
  try {
    const data = await createApiToken({
      name: newTokenName.value.trim(),
      scopes: newTokenScopes.value,
    })
    // Після створення — показуємо сирий токен і оновлюємо список
    newlyCreatedToken.value = data.data?.rawToken || null
    const refreshed = await fetchApiTokens()
    apiTokens.value = Array.isArray(refreshed.data) ? refreshed.data : []
    showCreateForm.value = false
    newTokenName.value = ''
    newTokenScopes.value = []
  } catch (err) {
    console.error('Failed to create API token:', err)
  } finally {
    creatingToken.value = false
  }
}

const handleRevokeToken = async (id) => {
  revokingTokenId.value = id
  try {
    await revokeApiToken(id)
    // Позначаємо токен як відкликаний локально, не перезавантажуємо весь список
    apiTokens.value = apiTokens.value.map(tok =>
      tok.id === id ? { ...tok, revokedAt: new Date().toISOString() } : tok
    )
  } catch (err) {
    console.error('Failed to revoke API token:', err)
  } finally {
    revokingTokenId.value = null
  }
}

// Починає редагування токена — заповнює форму поточними даними
const startEditToken = (token) => {
  editingTokenId.value = token.id
  editTokenName.value = token.name || ''
  editTokenScopes.value = [...(token.scopes || [])]
}

// Скасовує редагування без збереження
const cancelEditToken = () => {
  editingTokenId.value = null
  editTokenName.value = ''
  editTokenScopes.value = []
}

// Перемикає наявність scope у списку редагованих скопів
const toggleEditScope = (scope) => {
  const idx = editTokenScopes.value.indexOf(scope)
  if (idx >= 0) {
    editTokenScopes.value.splice(idx, 1)
  } else {
    editTokenScopes.value.push(scope)
  }
}

// Зберігає зміни назви та скопів токена через API
const handleSaveToken = async () => {
  if (savingToken.value || !editingTokenId.value) return
  if (!editTokenName.value.trim() || editTokenScopes.value.length === 0) return
  savingToken.value = true
  try {
    await updateApiToken(editingTokenId.value, {
      name: editTokenName.value.trim(),
      scopes: [...editTokenScopes.value],
    })
    editingTokenId.value = null
    await loadApiTokens()
  } catch (err) {
    console.error('Failed to update API token:', err)
  } finally {
    savingToken.value = false
  }
}

const copyTokenToClipboard = () => {
  if (newlyCreatedToken.value) {
    navigator.clipboard.writeText(newlyCreatedToken.value)
  }
}

const dismissNewToken = () => {
  newlyCreatedToken.value = null
}

const toggleApiTab = () => {
  activeTab.value = 'api'
  // Завантажуємо токени при першому відкритті вкладки
  if (apiTokens.value.length === 0 && !loadingTokens.value) {
    loadApiTokens()
  }
}

// Форматуємо дату в dd.mm.yyyy
const formatTokenDate = (isoString) => {
  if (!isoString) return '—'
  const d = new Date(isoString)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

const saveNotificationPrefs = async () => {
  notifPrefsError.value = ''
  notifPrefsMessage.value = ''
  notifPrefsSaving.value = true
  try {
    const result = await updateNotificationPreferences(notificationPrefs.value)
    notificationPrefs.value = result.preferences || {}
    notifPrefsMessage.value = t('settings.notifications.preferences.saved')
  } catch (err) {
    notifPrefsError.value = t('settings.notifications.preferences.error')
  } finally {
    notifPrefsSaving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="modal-backdrop" role="dialog" aria-modal="true">
      <div class="modal settings-modal" :class="displayOnly ? 'settings-modal--display' : 'modal--wide'">
        <div class="settings-modal__header">
        <div>
          <h2 class="modal__title">{{ displayOnly ? t('settings.displayTitle') : t('settings.title') }}</h2>
          <p class="modal__subtitle">{{ displayOnly ? t('settings.displaySubtitle') : t('settings.subtitle') }}</p>
        </div>
        <button class="icon-button settings-modal__close" type="button" :aria-label="t('settings.close')" @click="handleClose">
          <span class="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
      </div>

      <div v-if="loading" class="settings-loading">
        <span class="material-symbols-outlined settings-loading__icon" aria-hidden="true">progress_activity</span>
        <span>{{ t('settings.loading') }}</span>
      </div>

      <template v-else>
      <!-- Вкладки навігації — pill-стиль -->
      <div v-if="!displayOnly" class="settings-tabs">
        <button
          class="settings-tab"
          :class="{ 'settings-tab--active': activeTab === 'profile' }"
          type="button"
          @click="activeTab = 'profile'"
        >
          <span class="material-symbols-outlined settings-tab__icon">person</span>
          <span>{{ t('settings.tabs.profile') }}</span>
        </button>
        <button
          class="settings-tab"
          :class="{ 'settings-tab--active': activeTab === 'password' }"
          type="button"
          @click="activeTab = 'password'"
        >
          <span class="material-symbols-outlined settings-tab__icon">lock</span>
          <span>{{ t('settings.tabs.password') }}</span>
        </button>
        <button
          class="settings-tab"
          :class="{ 'settings-tab--active': activeTab === 'security' }"
          type="button"
          @click="activeTab = 'security'"
        >
          <span class="material-symbols-outlined settings-tab__icon">shield</span>
          <span>{{ t('settings.tabs.security') }}</span>
        </button>
        <button
          class="settings-tab"
          :class="{ 'settings-tab--active': activeTab === 'api' }"
          type="button"
          @click="toggleApiTab"
        >
          <span class="material-symbols-outlined settings-tab__icon">api</span>
          <span>{{ t('settings.tabs.api') }}</span>
        </button>
      </div>

      <div class="settings-content">
        <!-- Вкладка: Профіль -->
        <div v-show="activeTab === 'profile'" class="settings-tab-content">
          <div class="settings-layout">
            <!-- Левая колонка: Аватар -->
            <div v-if="!displayOnly" class="settings-avatar">
              <div class="avatar-upload__preview" :class="{ 'avatar-upload__preview--placeholder': !hasAvatar }">
                <img v-if="hasAvatar" :src="avatarPreview" alt="" />
                <span v-else>{{ (profile?.username || '').slice(0, 2).toUpperCase() }}</span>
              </div>
              <div class="avatar-upload__buttons">
                <label class="secondary avatar-upload__button">
                  <input class="file-input" type="file" accept="image/png,image/jpeg,image/webp" @change="onAvatarChange" />
                  {{ avatarUploading ? t('settings.avatar.uploading') : t('settings.avatar.upload') }}
                </label>
                <button
                  v-if="hasAvatar"
                  class="icon-button"
                  type="button"
                  :disabled="avatarDeleting"
                  @click="onAvatarDelete"
                  :aria-label="t('settings.avatar.delete')"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">delete</span>
                </button>
              </div>
              <p v-if="avatarError" class="alert alert--error alert--compact">{{ avatarError }}</p>
              <p v-if="avatarMessage" class="alert alert--info alert--compact">{{ avatarMessage }}</p>
            </div>

            <!-- Правая колонка: Поля -->
            <div class="settings-fields">
              <!-- Имя и email - данные аккаунта владельца, в режиме «только вид» скрыты -->
              <template v-if="!displayOnly">
              <label class="field">
                <span>{{ t('settings.profile.nameLabel') }}</span>
                <input
                  v-model="profileName"
                  class="field-input"
                  type="text"
                  :placeholder="t('settings.profile.namePlaceholder')"
                />
              </label>

              <div class="field-group">
                <label class="field">
                  <span>{{ t('settings.email.title') }}</span>
                  <input
                    v-model="emailInput"
                    class="field-input"
                    type="email"
                    :placeholder="t('settings.email.placeholder')"
                    autocomplete="off"
                  />
                </label>
                <button
                  v-if="isEmailChanged"
                  class="secondary"
                  type="button"
                  :disabled="emailSaving"
                  @click="submitEmailChange"
                >
                  {{ emailSaving ? t('settings.email.saving') : t('settings.email.submit') }}
                </button>
              </div>
              <span v-if="isEmailChanged" class="field-hint">{{ t('settings.email.helper') }}</span>
              <p v-if="pendingEmailLabel" class="alert alert--info alert--compact">{{ pendingEmailLabel }}</p>
              <p v-if="emailError" class="alert alert--error alert--compact">{{ emailError }}</p>
              <p v-if="emailMessage" class="alert alert--info alert--compact">{{ emailMessage }}</p>

              <!-- Адрес для счетов: бухгалтерия клиента, пусто = счета на email профиля -->
              <label class="field">
                <span>{{ t('settings.invoiceEmail.title') }}</span>
                <input
                  v-model="invoiceEmailInput"
                  class="field-input"
                  type="email"
                  autocomplete="off"
                />
              </label>
              <span class="field-hint">{{ t('settings.invoiceEmail.helper') }}</span>
              </template>

              <!-- Язык интерфейса (per-device, применяется перезагрузкой страницы).
                   Выпадающий список в стиле полей выше — глобальный .language-select -->
              <div class="field language-select">
                <label for="settings-interface-lang">{{ t('settings.interfaceLang.title') }}</label>
                <div class="select-wrapper">
                  <select
                    id="settings-interface-lang"
                    class="select"
                    :value="interfaceLanguage"
                    :disabled="languageSwitching"
                    @change="onInterfaceLanguageChange($event.target.value)"
                  >
                    <option v-for="code in availableLanguages" :key="code" :value="code" :lang="code">
                      {{ languageNames[code] }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Тема оформлення (per-device) -->
              <div class="theme-field">
                <span class="theme-field__label">{{ t('settings.theme.title') }}</span>
                <div class="theme-options" role="radiogroup" :aria-label="t('settings.theme.title')">
                  <label class="theme-option" :class="{ 'theme-option--active': themePreference === 'auto' }">
                    <input
                      type="radio"
                      name="theme"
                      value="auto"
                      :checked="themePreference === 'auto'"
                      @change="onThemeChange('auto')"
                    />
                    <span class="material-symbols-outlined theme-option__icon" aria-hidden="true">brightness_auto</span>
                    <span>{{ t('settings.theme.auto') }}</span>
                  </label>
                  <label class="theme-option" :class="{ 'theme-option--active': themePreference === 'light' }">
                    <input
                      type="radio"
                      name="theme"
                      value="light"
                      :checked="themePreference === 'light'"
                      @change="onThemeChange('light')"
                    />
                    <span class="material-symbols-outlined theme-option__icon" aria-hidden="true">light_mode</span>
                    <span>{{ t('settings.theme.light') }}</span>
                  </label>
                  <label class="theme-option" :class="{ 'theme-option--active': themePreference === 'dark' }">
                    <input
                      type="radio"
                      name="theme"
                      value="dark"
                      :checked="themePreference === 'dark'"
                      @change="onThemeChange('dark')"
                    />
                    <span class="material-symbols-outlined theme-option__icon" aria-hidden="true">dark_mode</span>
                    <span>{{ t('settings.theme.dark') }}</span>
                  </label>
                </div>
              </div>

              <!-- Размер текста (per-device) -->
              <div class="theme-field">
                <span class="theme-field__label">{{ t('settings.textSize.title') }}</span>
                <div class="theme-options" role="radiogroup" :aria-label="t('settings.textSize.title')">
                  <label
                    v-for="opt in textSizeOptions"
                    :key="opt.value"
                    class="theme-option"
                    :class="{ 'theme-option--active': textSizePreference === opt.value }"
                  >
                    <input
                      type="radio"
                      name="textSize"
                      :value="opt.value"
                      :checked="textSizePreference === opt.value"
                      @change="onTextSizeChange(opt.value)"
                    />
                    <span class="material-symbols-outlined theme-option__icon" aria-hidden="true">{{ opt.icon }}</span>
                    <span>{{ t(opt.labelKey) }}</span>
                  </label>
                </div>
              </div>

              <!-- Режим главного меню (per-device, только desktop - на мобильном скрыт) -->
              <div class="theme-field nav-layout-field">
                <span class="theme-field__label">{{ t('settings.navLayout.title') }}</span>
                <div class="theme-options" role="radiogroup" :aria-label="t('settings.navLayout.title')">
                  <label
                    v-for="opt in navLayoutOptions"
                    :key="opt.value"
                    class="theme-option"
                    :class="{ 'theme-option--active': navLayoutPreference === opt.value }"
                  >
                    <input
                      type="radio"
                      name="navLayout"
                      :value="opt.value"
                      :checked="navLayoutPreference === opt.value"
                      @change="onNavLayoutChange(opt.value)"
                    />
                    <span class="material-symbols-outlined theme-option__icon" aria-hidden="true">{{ opt.icon }}</span>
                    <span>{{ t(opt.labelKey) }}</span>
                  </label>
                </div>
              </div>

              <!-- Панель списка на карте для телефона (per-device; виден только на мобильном).
                   Режим применяет SidePanel на страницах Транспорт и Геозоны -->
              <div class="theme-field mobile-panel-field">
                <span class="theme-field__label">{{ t('settings.mobilePanel.title') }}</span>
                <div class="theme-options theme-options--two" role="radiogroup" :aria-label="t('settings.mobilePanel.title')">
                  <label
                    v-for="opt in mobilePanelOptions"
                    :key="opt.value"
                    class="theme-option"
                    :class="{ 'theme-option--active': mobilePanelPreference === opt.value }"
                  >
                    <input
                      type="radio"
                      name="mobilePanel"
                      :value="opt.value"
                      :checked="mobilePanelPreference === opt.value"
                      @change="onMobilePanelChange(opt.value)"
                    />
                    <span class="material-symbols-outlined theme-option__icon" aria-hidden="true">{{ opt.icon }}</span>
                    <span>{{ t(opt.labelKey) }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>


        <!-- Вкладка: Зміна пароля -->
        <div v-show="activeTab === 'password'" class="settings-tab-content password-section">
          <!-- Приховане поле username для менеджера паролів Chrome — без нього Chrome бере email з іншої вкладки -->
          <input type="text" :value="profile?.username" autocomplete="username" style="position:absolute;opacity:0;height:0;width:0;pointer-events:none;z-index:-1;" tabindex="-1" aria-hidden="true" />
          <label class="field">
            <span>{{ t('settings.password.current') }}</span>
            <input
              v-model="currentPassword"
              class="field-input"
              type="password"
              autocomplete="new-password"
              readonly
              @focus="$event.target.removeAttribute('readonly')"
            />
          </label>
          <label class="field">
            <span>{{ t('settings.password.new') }}</span>
            <input
              v-model="newPassword"
              class="field-input"
              type="password"
              autocomplete="new-password"
            />
          </label>
          <label class="field">
            <span>{{ t('settings.password.confirm') }}</span>
            <input
              v-model="confirmPassword"
              class="field-input"
              type="password"
              autocomplete="new-password"
            />
          </label>
          <button class="secondary" type="button" :disabled="passwordSaving" @click="submitPasswordChange">
            {{ passwordSaving ? t('settings.password.saving') : t('settings.password.submit') }}
          </button>
          <p v-if="passwordError" class="alert alert--error alert--compact">{{ passwordError }}</p>
          <p v-if="passwordMessage" class="alert alert--info alert--compact">{{ passwordMessage }}</p>
        </div>

        <!-- Вкладка: Безпека — активні сесії + довірені пристрої -->
        <div v-show="activeTab === 'security'" class="settings-tab-content security-section">
          <!-- Безпека: активні сесії + опція виходу з усіх -->
          <div class="security-block">
            <h4 class="security-block__title">{{ t('settings.security.title') }}</h4>
            <p class="security-block__hint">{{ t('settings.security.sessionsHint') }}</p>

            <!-- Список активних пристроїв -->
            <div v-if="sessionsLoading" class="sessions-state">{{ t('settings.security.sessionsLoading') }}</div>
            <p v-else-if="sessionsError" class="alert alert--error alert--compact">{{ sessionsError }}</p>
            <div v-else-if="sessions.length === 0" class="sessions-state">{{ t('settings.security.sessionsEmpty') }}</div>
            <ul v-else class="sessions-list">
              <li v-for="s in sessions" :key="s.id" class="session-item" :class="{ 'session-item--current': s.isCurrent }">
                <span class="material-symbols-outlined session-item__icon" aria-hidden="true">
                  {{ sessionDeviceIcon(s) }}
                </span>
                <div class="session-item__info">
                  <div class="session-item__title">
                    <span>{{ sessionDeviceLabel(s) }}</span>
                    <span v-if="s.isCurrent" class="session-item__badge">{{ t('settings.security.thisDevice') }}</span>
                  </div>
                  <div class="session-item__meta">
                    <span v-if="s.ip">{{ s.ip }}<span v-if="s.geo?.country"> · {{ s.geo.country }}</span></span>
                    <span>{{ t('settings.security.lastActive') }}: {{ formatSessionTime(s.lastUsedAt || s.issuedAt) }}</span>
                  </div>
                </div>
                <button
                  class="session-item__revoke"
                  type="button"
                  :disabled="revokingSessionId === s.id"
                  :title="s.isCurrent ? t('settings.security.revokeCurrent') : t('settings.security.revokeOne')"
                  @click="revokeOneSession(s)"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">
                    {{ revokingSessionId === s.id ? 'hourglass_top' : 'logout' }}
                  </span>
                </button>
              </li>
            </ul>

            <!-- Вийти з усіх пристроїв -->
            <div class="security-block__revoke-all">
              <template v-if="!revokeAllConfirming">
                <button class="danger-outline" type="button" @click="revokeAllConfirming = true">
                  {{ t('settings.security.revokeAll') }}
                </button>
              </template>
              <template v-else>
                <p class="security-block__confirm">{{ t('settings.security.revokeAllConfirm') }}</p>
                <div class="security-block__actions">
                  <button class="secondary" type="button" :disabled="revokeAllSaving" @click="revokeAllConfirming = false">
                    {{ t('common.cancel') }}
                  </button>
                  <button class="danger" type="button" :disabled="revokeAllSaving" @click="submitRevokeAllSessions">
                    {{ revokeAllSaving ? t('settings.security.revokeAllProgress') : t('settings.security.revokeAllConfirmBtn') }}
                  </button>
                </div>
              </template>
              <p v-if="revokeAllError" class="alert alert--error alert--compact">{{ revokeAllError }}</p>
            </div>
          </div>

          <!-- Довірені пристрої (device-trust): мітка client_dt проходить мережевий гейт -->
          <div class="security-block">
            <h4 class="security-block__title">{{ t('settings.trustedDevices.title') }}</h4>
            <p class="security-block__hint">{{ t('settings.trustedDevices.hint') }}</p>

            <div v-if="trustedDevicesLoading" class="sessions-state">{{ t('settings.security.sessionsLoading') }}</div>
            <p v-else-if="trustedDevicesError" class="alert alert--error alert--compact">{{ trustedDevicesError }}</p>
            <div v-else-if="trustedDevices.length === 0" class="sessions-state">{{ t('settings.trustedDevices.empty') }}</div>
            <ul v-else class="sessions-list">
              <li v-for="d in trustedDevices" :key="d.id" class="session-item" :class="{ 'session-item--current': d.isCurrent }">
                <span class="material-symbols-outlined session-item__icon" aria-hidden="true">
                  {{ sessionDeviceIcon(d) }}
                </span>
                <div class="session-item__info">
                  <div class="session-item__title">
                    <span>{{ sessionDeviceLabel({ ...d, userAgent: d.label }) }}</span>
                    <span v-if="d.isCurrent" class="session-item__badge">{{ t('settings.security.thisDevice') }}</span>
                  </div>
                  <div class="session-item__meta">
                    <span v-if="d.lastIp">{{ d.lastIp }}<span v-if="d.geo?.country"> · {{ d.geo.country }}</span></span>
                    <span>{{ t('settings.trustedDevices.lastUsed') }}: {{ formatSessionTime(d.lastUsedAt || d.createdAt) }}</span>
                  </div>
                </div>
                <button
                  class="session-item__revoke"
                  :class="{ 'session-item__revoke--confirm': deviceConfirmId === d.id }"
                  type="button"
                  :disabled="revokingDeviceId === d.id"
                  :title="deviceConfirmId === d.id ? t('settings.trustedDevices.revokeConfirm') : t('settings.trustedDevices.revoke')"
                  @click="revokeOneDevice(d)"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">
                    {{ revokingDeviceId === d.id ? 'hourglass_top' : (deviceConfirmId === d.id ? 'warning' : 'delete') }}
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        <!-- Вкладка: API-токени -->
        <div v-show="activeTab === 'api'" class="settings-tab-content api-section">

          <!-- Посилання на Swagger документацію. Відносний шлях: /api-docs/ роздається
               nginx на будь-якому хості портала (в т.ч. white label доменах) -->
          <a
            href="/api-docs/"
            target="_blank"
            rel="noopener noreferrer"
            class="api-docs-link"
          >
            <span class="material-symbols-outlined" aria-hidden="true">description</span>
            <span>{{ t('settings.api.docsLink') }}</span>
            <span class="material-symbols-outlined api-docs-link__arrow" aria-hidden="true">open_in_new</span>
          </a>

          <!-- Блок успішного створення токена — показуємо разово одразу після генерації -->
          <div v-if="newlyCreatedToken" class="api-token-created">
            <p class="api-token-created__warning">
              <span class="material-symbols-outlined" aria-hidden="true">warning</span>
              {{ t('settings.api.tokenWarning') }}
            </p>
            <div class="api-token-created__value">
              <code class="api-token-value">{{ newlyCreatedToken }}</code>
              <button
                class="icon-button"
                type="button"
                :aria-label="t('settings.api.copy')"
                @click="copyTokenToClipboard"
              >
                <span class="material-symbols-outlined" aria-hidden="true">content_copy</span>
              </button>
            </div>
            <button class="secondary" type="button" @click="dismissNewToken">
              {{ t('settings.api.done') }}
            </button>
          </div>

          <!-- Список існуючих токенів -->
          <div v-if="loadingTokens" class="api-tokens-loading">
            <span class="material-symbols-outlined api-tokens-loading__icon" aria-hidden="true">progress_activity</span>
            <span>{{ t('settings.api.loading') }}</span>
          </div>

          <template v-else>
            <div v-if="apiTokens.length === 0 && !showCreateForm" class="api-tokens-empty">
              {{ t('settings.api.empty') }}
            </div>

            <div v-else class="api-tokens-list">
              <div
                v-for="token in apiTokens"
                :key="token.id"
                class="api-token-card"
                :class="{ 'api-token-card--revoked': token.revokedAt }"
              >
                <!-- Форма редагування токена (назва + скопи) -->
                <div v-if="editingTokenId === token.id" class="api-token-edit">
                  <div class="form-group">
                    <label class="form-label">{{ t('settings.api.name') }}</label>
                    <input
                      v-model="editTokenName"
                      type="text"
                      class="form-input"
                      maxlength="100"
                    />
                  </div>
                  <div class="form-group">
                    <label class="form-label">{{ t('settings.api.scopes') }}</label>
                    <div class="api-scope-groups">
                      <div v-for="group in TOKEN_SCOPE_GROUPS" :key="group.label" class="api-scope-group">
                        <span class="api-scope-group__label">{{ t(group.label) }}</span>
                        <label
                          v-for="scope in group.scopes"
                          :key="scope"
                          class="api-scope-checkbox"
                        >
                          <input
                            type="checkbox"
                            :checked="editTokenScopes.includes(scope)"
                            @change="toggleEditScope(scope)"
                          />
                          <span>{{ scope }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div class="api-token-edit__actions">
                    <button
                      class="button button--sm button--primary"
                      type="button"
                      :disabled="savingToken || !editTokenName.trim() || editTokenScopes.length === 0"
                      @click="handleSaveToken"
                    >
                      {{ savingToken ? t('settings.api.saving') : t('settings.api.save') }}
                    </button>
                    <button class="button button--sm secondary" type="button" @click="cancelEditToken">
                      {{ t('settings.api.cancel') }}
                    </button>
                  </div>
                </div>

                <!-- Звичайний перегляд картки токена -->
                <template v-else>
                  <div class="api-token-card__header">
                    <span class="api-token-card__name">{{ token.name }}</span>
                    <span v-if="token.revokedAt" class="api-token-card__revoked-label">
                      {{ t('settings.api.revoked') }}
                    </span>
                    <template v-else>
                      <button
                        class="secondary api-token-card__edit-btn"
                        type="button"
                        :disabled="!!editingTokenId"
                        @click="startEditToken(token)"
                      >
                        {{ t('settings.api.edit') }}
                      </button>
                      <button
                        class="secondary api-token-card__revoke-btn"
                        type="button"
                        :disabled="revokingTokenId === token.id || !!editingTokenId"
                        @click="handleRevokeToken(token.id)"
                      >
                        {{ revokingTokenId === token.id ? t('common.loading') : t('settings.api.revoke') }}
                      </button>
                    </template>
                  </div>
                  <div class="api-token-card__meta">
                    <!-- Префікс токена в моноширинному шрифті -->
                    <span class="api-token-card__prefix">{{ token.prefix }}</span>
                    <!-- Скопи у вигляді пігулок -->
                    <div class="api-token-card__scopes">
                      <span
                        v-for="scope in (token.scopes || [])"
                        :key="scope"
                        class="api-scope-tag"
                      >{{ scope }}</span>
                    </div>
                  </div>
                  <div class="api-token-card__dates">
                    <span>{{ t('settings.api.created') }}: {{ formatTokenDate(token.createdAt) }}</span>
                    <span v-if="token.lastUsedAt">{{ t('settings.api.lastUsed') }}: {{ formatTokenDate(token.lastUsedAt) }}</span>
                    <span v-else class="api-token-card__never-used">{{ t('settings.api.neverUsed') }}</span>
                  </div>
                </template>
              </div>
            </div>
          </template>

          <!-- Форма створення токена -->
          <div v-if="showCreateForm" class="api-create-form">
            <h4 class="section-title">{{ t('settings.api.createTitle') }}</h4>
            <label class="field">
              <span>{{ t('settings.api.nameLabel') }}</span>
              <input
                v-model="newTokenName"
                class="field-input"
                type="text"
                :placeholder="t('settings.api.namePlaceholder')"
                maxlength="80"
              />
            </label>

            <div class="api-scopes-label">{{ t('settings.api.scopesLabel') }}</div>
            <div class="api-scope-groups">
              <div
                v-for="group in TOKEN_SCOPE_GROUPS"
                :key="group.label"
                class="api-scope-group"
              >
                <span class="api-scope-group__label">{{ t(group.label) }}</span>
                <label
                  v-for="scope in group.scopes"
                  :key="scope"
                  class="api-scope-checkbox"
                >
                  <input
                    type="checkbox"
                    :value="scope"
                    v-model="newTokenScopes"
                  />
                  <span>{{ scope }}</span>
                </label>
              </div>
            </div>

            <div class="api-create-form__actions">
              <button
                class="primary"
                type="button"
                :disabled="creatingToken || !newTokenName.trim() || newTokenScopes.length === 0"
                @click="handleCreateToken"
              >
                {{ creatingToken ? t('common.loading') : t('settings.api.create') }}
              </button>
              <button class="secondary" type="button" @click="showCreateForm = false">
                {{ t('common.cancel') }}
              </button>
            </div>
          </div>

          <!-- Кнопка відкриття форми створення -->
          <button
            v-if="!showCreateForm && !newlyCreatedToken"
            class="secondary api-section__create-btn"
            type="button"
            @click="showCreateForm = true"
          >
            <span class="material-symbols-outlined" aria-hidden="true">add</span>
            {{ t('settings.api.createButton') }}
          </button>
        </div>
      </div>

      <!-- Футер з кнопкою збереження (зафіксований внизу) — прихований на вкладці API -->
      <div v-if="activeTab !== 'api' && !displayOnly" class="settings-footer">
        <p v-if="profileError" class="alert alert--error">{{ profileError }}</p>
        <button class="primary" type="button" :disabled="savingProfile" @click="saveProfile">
          {{ savingProfile ? t('settings.profile.saving') : t('settings.profile.save') }}
        </button>
      </div>
      </template>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* === Theme selector === */
.theme-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.theme-field__label {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--text-secondary);
}

.theme-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.theme-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.75rem 0.5rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: var(--text-sm);
  color: var(--text-primary);
  background: var(--bg-card);
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;
}

.theme-option:hover {
  border-color: var(--border-strong);
  background: var(--state-hover);
}

.theme-option--active {
  border-color: var(--primary);
  background: var(--primary-bg);
  color: var(--primary-text);
}

.theme-option input[type="radio"] {
  /* Прячем нативный radio — статус показываем через стили обёртки */
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.theme-option__icon {
  font-size: 1.5rem;
}

/* Режим меню имеет смысл только на desktop (>= 960px): на мобильном всегда бургер */
@media (max-width: 959px) {
  .nav-layout-field {
    display: none;
  }
}

/* Панель на карте настраивается только для телефона (< 768px): на десктопе она всегда сбоку */
@media (min-width: 768px) {
  .mobile-panel-field {
    display: none;
  }
}

.theme-options--two {
  grid-template-columns: repeat(2, 1fr);
}

.settings-modal {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: calc(100vh - 3rem);
  max-height: calc(100dvh - 3rem);
  padding-bottom: 0;
}

.settings-modal__header {
  flex-shrink: 0;
}

.settings-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.75rem 1.5rem 1.5rem;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

/* Вкладки — зафіксовані поза скролом */
.settings-tabs {
  display: flex;
  gap: 0.375rem;
  padding: 0 1.5rem;
  flex-shrink: 0;
  margin-bottom: 0.25rem;
}

.settings-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.75rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  transition: color 0.2s, border-color 0.2s;
}

.settings-tab:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-strong);
}

.settings-tab--active {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--text-inverse);
  box-shadow: 0 1px 4px rgba(59, 91, 219, 0.3);
}

.settings-tab--active:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
}

.settings-tab__icon {
  font-size: var(--text-lg);
}

.settings-tab--active .settings-tab__icon {
  color: var(--text-inverse);
}

.settings-tab-content {
  flex: 1;
  min-height: 0;
}

.settings-layout {
  display: flex;
  gap: 2rem;
}

.settings-avatar {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 140px;
}

.settings-avatar .avatar-upload__preview {
  width: 100px;
  height: 100px;
}

.settings-avatar .avatar-upload__buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.settings-avatar .avatar-upload__button {
  font-size: var(--text-sm);
  padding: 0.375rem 0.75rem;
}

.settings-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field-group {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
}

.field-group .field {
  flex: 1;
}

.field-group .secondary {
  flex-shrink: 0;
  margin-bottom: 0;
}

.field-hint {
  display: block;
  margin-top: 0.25rem;
  font-size: var(--text-sm);
  color: var(--text-secondary, var(--text-muted));
}

.password-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.password-section .secondary {
  align-self: flex-start;
}

/* Вкладка «Безпека»: перший блок без верхнього розділювача (бордер лишається між блоками) */
.security-section .security-block:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

/* Безпека: блок «Вийти з усіх пристроїв» */
.security-block {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-default);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.security-block__title {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-heading);
}
.security-block__hint {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.4;
}
.security-block__confirm {
  margin: 0.25rem 0 0;
  font-size: var(--text-sm);
  color: var(--text-primary);
  font-weight: 500;
}
.security-block__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.security-block .danger-outline,
.security-block .danger {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.85rem;
  font-size: var(--text-sm);
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.security-block .danger-outline {
  background: transparent;
  color: var(--danger);
  border: 1px solid var(--danger);
  align-self: flex-start;
}
.security-block .danger-outline:hover {
  background: var(--danger-light);
}
.security-block .danger {
  background: var(--danger);
  color: #fff;
  border: 1px solid var(--danger);
}
.security-block .danger:hover:not(:disabled) {
  background: var(--danger-hover);
}
.security-block .danger:disabled,
.security-block .danger-outline:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.security-block .material-symbols-outlined {
  font-size: 1.1rem;
}

/* Список активних сесій */
.sessions-state {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  padding: 0.5rem 0;
}
.sessions-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.session-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--bg-card);
}
.session-item--current {
  border-color: var(--primary);
  background: var(--primary-bg, var(--bg-card));
}
.session-item__icon {
  flex-shrink: 0;
  font-size: 1.4rem !important;
  color: var(--text-secondary);
}
.session-item--current .session-item__icon {
  color: var(--primary);
}
.session-item__info {
  flex: 1;
  min-width: 0;
}
.session-item__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-primary);
}
.session-item__badge {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.1rem 0.4rem;
  border-radius: 6px;
  background: var(--primary);
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.session-item__meta {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.2rem;
}
.session-item__revoke {
  flex-shrink: 0;
  background: transparent;
  border: 1px solid var(--border-default);
  color: var(--text-secondary);
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}
.session-item__revoke:hover:not(:disabled) {
  color: var(--danger);
  border-color: var(--danger);
  background: var(--danger-light);
}
/* Двокроковий відклик довіреного пристрою: перший клік підсвічує кнопку як підтвердження */
.session-item__revoke--confirm {
  color: var(--danger);
  border-color: var(--danger);
  background: var(--danger-light);
}
.session-item__revoke:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.security-block__revoke-all {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px dashed var(--border-light);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Секция оповещений */
.notifications-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-title {
  font-size: var(--text-sm);
  font-weight: 600;
  margin: 0 0 0.25rem;
  color: var(--text-primary, var(--gray-800));
}

/* Telegram (поддержка нескольких аккаунтов) */
.telegram-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.telegram-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.telegram-count {
  font-size: var(--text-xs);
  color: var(--text-secondary, var(--text-muted));
  background: var(--bg-secondary, var(--bg-tertiary));
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
}

.telegram-accounts-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.telegram-account-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: var(--bg-secondary, var(--bg-tertiary));
  border-radius: 0.25rem;
}

.telegram-username {
  font-weight: 500;
  color: var(--primary));
}

.telegram-deeplink {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--primary));
  text-decoration: none;
  font-weight: 500;
}

.telegram-deeplink:hover {
  text-decoration: underline;
}

.telegram-hint {
  margin-top: 0.5rem;
}

.telegram-code-display {
  display: block;
  background: var(--bg-secondary, var(--bg-tertiary));
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  user-select: all;
  cursor: pointer;
}

/* Таблица настроек уведомлений */
.notification-prefs {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.prefs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
  margin: 0.5rem 0;
}

.prefs-table th,
.prefs-table td {
  padding: 0.5rem;
  text-align: left;
  border-bottom: 1px solid var(--border-color, var(--border-default));
}

.prefs-table th {
  font-weight: 500;
  color: var(--text-secondary, var(--text-muted));
  font-size: var(--text-sm);
}

.prefs-table td:not(:first-child) {
  text-align: center;
}

.prefs-table th:not(:first-child) {
  text-align: center;
}

.prefs-table input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  accent-color: var(--primary);
}

.prefs-table input[type="checkbox"]:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.notification-prefs .secondary {
  align-self: flex-start;
  margin-top: 0.5rem;
}

.alert--compact {
  padding: 0.375rem 0.625rem;
  font-size: var(--text-sm);
}

.settings-footer {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color, var(--border-default));
  flex-shrink: 0;
  background: var(--bg-card);
}

.settings-footer .alert {
  width: 100%;
}

@media (max-width: 640px) {
  .settings-tabs {
    gap: 0.25rem;
    padding: 0 1rem;
    flex-wrap: wrap;
  }

  .settings-tab {
    padding: 0.375rem 0.625rem;
    font-size: var(--text-xs);
  }

  .settings-tab__icon {
    font-size: var(--text-base);
  }

  .settings-layout {
    flex-direction: column;
    gap: 1.5rem;
  }

  .settings-avatar {
    width: 100%;
    flex-direction: row;
    gap: 1rem;
  }

  .settings-avatar .avatar-upload__preview {
    width: 80px;
    height: 80px;
  }

  .settings-avatar .avatar-upload__buttons {
    flex-direction: row;
  }

  .field-group {
    flex-direction: column;
    align-items: stretch;
  }

  .prefs-table {
    font-size: var(--text-sm);
  }

  .prefs-table th,
  .prefs-table td {
    padding: 0.375rem 0.25rem;
  }
}

/* === API-токени === */
.api-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Посилання на Swagger API docs */
.api-docs-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(90deg, var(--primary-bg) 0%, #fff 100%);
  border: 1px solid var(--blue-100);
  border-left: 3px solid var(--primary);
  border-radius: var(--radius-md);
  color: var(--primary-text);
  font-size: var(--text-sm);
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s;
}

.api-docs-link:hover {
  background: linear-gradient(90deg, var(--primary-light) 0%, var(--bg-secondary) 100%);
  border-color: var(--blue-400);
  border-left-color: var(--primary-hover);
}

.api-docs-link .material-symbols-outlined {
  font-size: 20px;
  color: var(--primary);
}

.api-docs-link__arrow {
  margin-left: auto;
  font-size: 16px !important;
  color: var(--blue-400) !important;
}

.api-section__create-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.api-section__create-btn .material-symbols-outlined {
  font-size: 1rem;
}

/* Завантаження токенів */
.api-tokens-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary, var(--text-muted));
  font-size: var(--text-sm);
}

.api-tokens-loading__icon {
  font-size: var(--text-xl);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Порожній стан */
.api-tokens-empty {
  padding: 1.5rem 0;
  color: var(--text-secondary, var(--text-muted));
  font-size: var(--text-sm);
  text-align: center;
}

/* Список токенів */
.api-tokens-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

/* Картка токена */
.api-token-card {
  border: 1px solid var(--border-color, var(--border-default));
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  transition: opacity 0.2s;
}

.api-token-card--revoked {
  opacity: 0.5;
}

.api-token-card__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.api-token-card__name {
  font-weight: 600;
  font-size: var(--text-sm);
  flex: 1;
  color: var(--text-primary, var(--gray-800));
}

.api-token-card__revoked-label {
  font-size: var(--text-xs);
  color: var(--text-secondary, var(--text-muted));
  background: var(--bg-secondary, var(--bg-tertiary));
  padding: 0.1rem 0.5rem;
  border-radius: var(--radius-sm);
}

.api-token-card__revoke-btn {
  flex-shrink: 0;
  font-size: var(--text-sm);
  padding: 0.25rem 0.625rem;
}

.api-token-card__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Префікс токена в монопросторовому шрифті */
.api-token-card__prefix {
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  color: var(--text-secondary, var(--text-muted));
  background: var(--bg-secondary, var(--bg-tertiary));
  padding: 0.1rem 0.35rem;
  border-radius: var(--radius-sm);
}

/* Скопи — пігулки */
.api-token-card__scopes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.api-scope-tag {
  font-size: var(--text-xs);
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  padding: 0.1rem 0.35rem;
  border-radius: var(--radius-sm);
  font-weight: 500;
}

.api-token-card__dates {
  display: flex;
  gap: 1rem;
  font-size: var(--text-xs);
  color: var(--text-secondary, var(--text-muted));
  flex-wrap: wrap;
}

.api-token-card__never-used {
  font-style: italic;
}

/* Блок після успішного створення токена */
.api-token-created {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--amber-400);
  background: var(--amber-50);
  border-radius: var(--radius-md);
}

.api-token-created__warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--warning-text);
  margin: 0;
}

.api-token-created__warning .material-symbols-outlined {
  font-size: 1.125rem;
  flex-shrink: 0;
}

.api-token-created__value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.75rem;
}

.api-token-value {
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  word-break: break-all;
  flex: 1;
  color: var(--gray-800);
  user-select: all;
}

.api-token-created .secondary {
  align-self: flex-start;
}

/* Форма створення токена */
.api-create-form {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1rem;
  border: 1px dashed var(--border-color, var(--border-default));
  border-radius: var(--radius-md);
}

.api-scopes-label {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-primary, var(--gray-800));
}

.api-scope-groups {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.api-scope-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem 0.75rem;
}

.api-scope-group__label {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary, var(--text-muted));
  min-width: 90px;
  flex-shrink: 0;
}

.api-scope-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: var(--text-sm);
  cursor: pointer;
  color: var(--text-primary, var(--gray-800));
}

.api-scope-checkbox input[type="checkbox"] {
  width: 0.875rem;
  height: 0.875rem;
  cursor: pointer;
}

.api-create-form__actions {
  display: flex;
  gap: 0.625rem;
  align-items: center;
}

@media (max-width: 640px) {
  .api-token-card__dates {
    gap: 0.5rem;
  }

  .api-scope-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .api-scope-group__label {
    min-width: unset;
  }

  .api-create-form__actions {
    flex-direction: column;
    align-items: stretch;
  }
}

/* Кнопка редагування токена */
.api-token-card__edit-btn {
  flex-shrink: 0;
  font-size: var(--text-sm);
  padding: 0.25rem 0.625rem;
}

/* Інлайн-форма редагування токена всередині картки */
.api-token-edit {
  padding: 0.5rem 0;
}

.api-token-edit .form-group {
  margin-bottom: 0.6rem;
}

.api-token-edit .form-label {
  display: block;
  font-size: var(--text-sm);
  font-weight: 600;
  margin-bottom: 0.3rem;
  color: var(--text-muted, var(--text-muted));
}

.api-token-edit .form-input {
  width: 100%;
  max-width: 320px;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--border, var(--border-default));
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  box-sizing: border-box;
}

.api-token-edit__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

/* Mobile fullscreen */
@media (max-width: 520px) {
  .modal-backdrop {
    padding: 0;
    align-items: stretch;
  }

  .settings-modal {
    max-width: 100%;
    width: 100%;
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
    max-height: 100dvh;
    height: 100vh;
    height: 100dvh;
  }

  .settings-tabs {
    padding: 0 1rem;
  }

  .settings-content {
    flex: 1;
    min-height: 0;
    padding: 0.75rem 1rem 1rem;
  }

  /* Учитываем "челку" iPhone (safe area) — на мобильном модалка fullscreen, backdrop padding=0,
     поэтому safe-area подвешиваем на сам header модалки. */
  .settings-modal__header {
    padding-top: calc(0.5rem + env(safe-area-inset-top, 0px));
  }

  .settings-footer {
    padding: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  }

  /* В режиме «только вид» футера нет: отступ под полоску iPhone даёт сам контент */
  .settings-modal--display .settings-content {
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  }
}
</style>
